import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';
import { handleUnauthorized } from '../../services/api';
import { LayoutTemplate, Filter, Search, Plus, Calendar, Download, SlidersHorizontal, Trash2, ChevronLeft, ChevronRight, Send, Ban, CheckCircle, ArrowUpCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { format, isToday, isYesterday, differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears } from 'date-fns';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { useDocumentCategories } from '../../hooks/useDocumentCategories';
import { useTranslation } from 'react-i18next';
import SendTemplateModal from '../../components/template-editor/send-template-modal';
import { usePlan } from '../../contexts/PlanContext';
import UpgradePrompt from '../../components/UpgradePrompt';

interface Category {
  id: string;
  name: string;
}

interface Template {
  id: string;
  template_title: string;
  category?: Category;
  document_category_id?: string;
  file_path: string;
  status: string;
  sent_count: number;
  signer_count: number;
  recipient_count: number;
  last_used: string | null;
  created_at: string;
  updated_at: string;
  notes?: string;
}

const Templates: React.FC = () => {
  const { t } = useTranslation(['templates', 'common']);
  const location = useLocation();
  const navigate = useNavigate();

  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    return {
      timeFilter: params.get('timeFilter') || 'all',
      categoryFilter: params.get('categoryId') || 'all',
      statusFilter: params.get('status') || 'all',
      searchTerm: params.get('search') || '',
    };
  };

  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<{ current_page: number; last_page: number; per_page: number; total: number } | null>(null);
  const [timeFilter, setTimeFilter] = useState<string>(getInitialFilters().timeFilter);
  const [categoryFilter, setCategoryFilter] = useState<string>(getInitialFilters().categoryFilter);
  const [statusFilter, setStatusFilter] = useState<string>(getInitialFilters().statusFilter);
  const [searchTerm, setSearchTerm] = useState<string>(getInitialFilters().searchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>(getInitialFilters().searchTerm);

  const { categories: fetchedCategories } = useDocumentCategories();
  const { isAtLimit } = usePlan();
  const templateLimitReached = isAtLimit('templates');

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [templateToSend, setTemplateToSend] = useState<Template | null>(null);
  const [toggleDialogOpen, setToggleDialogOpen] = useState(false);
  const [templateToToggle, setTemplateToToggle] = useState<Template | null>(null);
  const [toggling, setToggling] = useState(false);

  const categoriesArray = Array.isArray(fetchedCategories) ? fetchedCategories :
    (fetchedCategories && 'categories' in fetchedCategories) ?
      (fetchedCategories as any).categories : [];

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (timeFilter !== 'all') params.set('timeFilter', timeFilter);
    if (categoryFilter !== 'all') params.set('categoryId', categoryFilter);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());

    navigate({ search: params.toString() }, { replace: true });
    fetchTemplates();
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm, currentPage]);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (statusFilter !== 'all') queryParams.set('status', statusFilter);
      if (categoryFilter !== 'all') queryParams.set('category_id', categoryFilter);
      if (timeFilter !== 'all') queryParams.set('time_period', timeFilter);
      if (debouncedSearchTerm.trim().length >= 3) queryParams.set('search', debouncedSearchTerm.trim());
      queryParams.set('page', currentPage.toString());

      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      const response = await fetch(
        `${apiUrl}/api/templates?${queryParams.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      if (data) {
        setTemplates(data.data || []);
        setPaginationMeta({
          current_page: data.current_page,
          last_page: data.last_page,
          per_page: data.per_page,
          total: data.total,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [timeFilter, categoryFilter, statusFilter, debouncedSearchTerm]);

  const templateStatuses = [
    { value: 'all', label: t('common:status.all') },
    { value: 'active', label: t('common:status.active') },
    { value: 'inactive', label: t('common:status.inactive') },
  ];

  const timePeriods = [
    { value: 'week', label: t('common:time.this_week') },
    { value: 'month', label: t('common:time.this_month') },
    { value: 'quarter', label: t('common:time.this_quarter') },
    { value: 'year', label: t('common:time.this_year') },
    { value: 'all', label: t('common:time.all_time') }
  ];

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">{t('common:status.active')}</Badge>;
      case 'inactive':
        return <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">{t('common:status.inactive')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleDeleteClick = (tmpl: Template) => {
    setTemplateToDelete(tmpl);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    setDeleting(true);
    try {
      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      const response = await fetch(`${apiUrl}/api/templates/${templateToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete template');
      }
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to delete template');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleClick = (tmpl: Template) => {
    setTemplateToToggle(tmpl);
    setToggleDialogOpen(true);
  };

  const handleToggleConfirm = async () => {
    if (!templateToToggle) return;
    setToggling(true);
    try {
      const apiUrl = API_BASE_URL.replace(/\/api$/, '');
      const response = await fetch(`${apiUrl}/api/templates/${templateToToggle.id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) { handleUnauthorized(); return; }
        const data = await response.json();
        throw new Error(data.message || 'Failed to update template status');
      }
      setToggleDialogOpen(false);
      setTemplateToToggle(null);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to update template status');
    } finally {
      setToggling(false);
    }
  };

  const formatFullDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };

  const formatRelativeDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();

      if (isToday(date)) return format(date, 'h:mm a');
      if (isYesterday(date)) return t('common:time.yesterday');

      const daysDiff = differenceInDays(now, date);
      if (daysDiff < 7) return t('common:time.days_ago', { count: daysDiff });

      const weeksDiff = differenceInWeeks(now, date);
      if (weeksDiff === 1) return t('common:time.last_week');
      if (weeksDiff < 4) return t('common:time.weeks_ago', { count: weeksDiff });

      const monthsDiff = differenceInMonths(now, date);
      if (monthsDiff === 1) return t('common:time.last_month');
      if (monthsDiff < 12) return t('common:time.months_ago', { count: monthsDiff });

      const yearsDiff = differenceInYears(now, date);
      if (yearsDiff === 1) return t('common:time.last_year');
      return t('common:time.years_ago', { count: yearsDiff });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Page header */}
      <div className="flex flex-col lg:flex-row justify-between gap-4 items-start lg:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('templates:page_title')}</h1>
          <p className="text-muted-foreground">{t('templates:page_subtitle')}</p>
        </div>

        <div className="flex flex-row gap-3">
          <Button
            variant="outline"
            className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
            onClick={() => setShowFilters((prev) => !prev)}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4 text-indigo-500" />
            {showFilters ? t('templates:hide_filters') : t('templates:show_filters')}
          </Button>
          {templateLimitReached ? (
            <Link to="/settings?tab=billing">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                <ArrowUpCircle className="mr-2 h-4 w-4" />
                {t('billing:upgrade.button', 'Upgrade Plan')}
              </Button>
            </Link>
          ) : (
            <Link to="/template-editor">
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                <Plus className="mr-2 h-4 w-4" />
                {t('templates:new_template')}
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Upgrade prompt when template limit reached */}
      {templateLimitReached && (
        <UpgradePrompt feature="templates" />
      )}

      {/* Filters section */}
      {showFilters && <Card className="border-indigo-100 bg-gradient-to-r from-indigo-50/50 via-white to-purple-50/50 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-indigo-100">
              <SlidersHorizontal className="h-4 w-4 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{t('templates:filters_title')}</CardTitle>
              <CardDescription>{t('templates:filters_description')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-amber-700">
                <Calendar className="h-3.5 w-3.5" />
                {t('templates:time_period')}
              </label>
              <Select value={timeFilter} onValueChange={setTimeFilter}>
                <SelectTrigger className="bg-white border-amber-200 focus:ring-amber-300">
                  <SelectValue placeholder={t('templates:select_period')} />
                </SelectTrigger>
                <SelectContent>
                  {timePeriods.map(period => (
                    <SelectItem key={period.value} value={period.value}>{period.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-blue-700">
                <LayoutTemplate className="h-3.5 w-3.5" />
                {t('templates:category')}
              </label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="bg-white border-blue-200 focus:ring-blue-300">
                  <SelectValue placeholder={t('templates:select_category')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('templates:all_categories')}</SelectItem>
                  {categoriesArray.map((category: { id: string, name: string }) => (
                    <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-purple-700">
                <Filter className="h-3.5 w-3.5" />
                {t('templates:status')}
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-white border-purple-200 focus:ring-purple-300">
                  <SelectValue placeholder={t('templates:select_status')} />
                </SelectTrigger>
                <SelectContent>
                  {templateStatuses.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex-1">
              <label className="text-sm font-medium mb-1 flex items-center gap-1.5 text-emerald-700">
                <Search className="h-3.5 w-3.5" />
                {t('templates:search')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400" />
                <Input
                  placeholder={t('templates:search_placeholder')}
                  className="pl-9 bg-white border-emerald-200 focus:ring-emerald-300"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>}

      {/* Templates table */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              <p>{error}</p>
              <Button variant="outline" onClick={fetchTemplates} className="mt-4">
                {t('common:actions.try_again')}
              </Button>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <LayoutTemplate className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{t('templates:no_templates_title')}</h3>
              <p className="text-muted-foreground mb-6">{t('templates:no_templates_message')}</p>
              <div className="flex justify-center gap-3">
                {!showFilters && (
                  <Button
                    variant="outline"
                    className="bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                    onClick={() => setShowFilters(true)}
                  >
                    <SlidersHorizontal className="mr-2 h-4 w-4 text-indigo-500" />
                    {t('templates:show_filters')}
                  </Button>
                )}
                {templateLimitReached ? (
                  <Link to="/settings?tab=billing">
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                      <ArrowUpCircle className="mr-2 h-4 w-4" />
                      {t('billing:upgrade.button', 'Upgrade Plan')}
                    </Button>
                  </Link>
                ) : (
                  <Link to="/template-editor">
                    <Button className="bg-green-600 hover:bg-green-700 text-white">
                      <Plus className="mr-2 h-4 w-4" />
                      {t('templates:new_template')}
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('templates:table_title')}</TableHead>
                    <TableHead>{t('templates:table_category')}</TableHead>
                    <TableHead className="text-center">{t('templates:table_recipients')}</TableHead>
                    <TableHead>{t('templates:table_status')}</TableHead>
                    <TableHead className="text-center">{t('templates:table_sent')}</TableHead>
                    <TableHead className="text-center">{t('templates:table_signed')}</TableHead>
                    <TableHead>{t('templates:table_last_used')}</TableHead>
                    <TableHead>{t('templates:table_created')}</TableHead>
                    <TableHead className="text-center">{t('templates:table_actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {templates.map((tmpl) => (
                    <TableRow
                      key={tmpl.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/template-editor/${tmpl.id}`)}
                    >
                      <TableCell className="font-medium max-w-[200px] truncate">{tmpl.template_title}</TableCell>
                      <TableCell>{tmpl.category?.name || t('templates:uncategorized')}</TableCell>
                      <TableCell className="text-center">{tmpl.recipient_count ?? 1}</TableCell>
                      <TableCell>{getStatusBadge(tmpl.status)}</TableCell>
                      <TableCell className="text-center">{tmpl.sent_count}</TableCell>
                      <TableCell className="text-center">{tmpl.signer_count}</TableCell>
                      <TableCell className="whitespace-nowrap" title={tmpl.last_used ? formatFullDate(tmpl.last_used) : ''}>
                        {tmpl.last_used ? formatRelativeDate(tmpl.last_used) : t('templates:never_used')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap" title={formatFullDate(tmpl.created_at)}>
                        {formatRelativeDate(tmpl.created_at)}
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-blue-50"
                            title={t('common:actions.send')}
                            disabled={tmpl.status !== 'active'}
                            onClick={() => {
                              setTemplateToSend(tmpl);
                              setSendModalOpen(true);
                            }}
                          >
                            <Send className={`h-4 w-4 ${tmpl.status === 'active' ? 'text-blue-600' : 'text-gray-400'}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-green-50"
                            title={t('common:actions.download')}
                            onClick={async () => {
                              try {
                                const response = await fetch(`${API_BASE_URL}/templates/${tmpl.id}/stream`, {
                                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                                });
                                if (!response.ok) {
                                  if (response.status === 401) { handleUnauthorized(); return; }
                                  throw new Error('Download failed');
                                }
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = window.document.createElement('a');
                                a.href = url;
                                a.download = `${tmpl.template_title}.pdf`;
                                window.document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                a.remove();
                              } catch (err) {
                                console.error('Download error:', err);
                              }
                            }}
                          >
                            <Download className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-8 w-8 p-0 ${tmpl.status === 'active' ? 'hover:bg-amber-50' : 'hover:bg-emerald-50'}`}
                            title={tmpl.status === 'active' ? t('templates:disable_title') : t('templates:enable_title')}
                            onClick={() => handleToggleClick(tmpl)}
                          >
                            {tmpl.status === 'active' ? (
                              <Ban className="h-4 w-4 text-amber-600" />
                            ) : (
                              <CheckCircle className="h-4 w-4 text-emerald-600" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 hover:bg-red-50"
                            title={t('common:actions.delete')}
                            onClick={() => handleDeleteClick(tmpl)}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {paginationMeta && paginationMeta.last_page > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-muted-foreground">
                {t('templates:showing_templates', {
                  start: ((paginationMeta.current_page - 1) * paginationMeta.per_page) + 1,
                  end: Math.min(paginationMeta.current_page * paginationMeta.per_page, paginationMeta.total),
                  total: paginationMeta.total
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-4 w-4" />
                  {t('common:actions.previous')}
                </Button>
                <span className="text-sm font-medium">
                  {t('common:pagination.page_of', { current: paginationMeta.current_page, last: paginationMeta.last_page })}
                </span>
                <Button variant="outline" size="sm" disabled={currentPage >= paginationMeta.last_page} onClick={() => setCurrentPage((p) => Math.min(paginationMeta.last_page, p + 1))}>
                  {t('common:actions.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation modal */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('templates:delete_title')}</DialogTitle>
            <DialogDescription>
              {t('templates:delete_message', { title: templateToDelete?.template_title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} disabled={deleting}>
              {deleting ? t('templates:deleting') : t('templates:delete_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toggle status confirmation modal */}
      <Dialog open={toggleDialogOpen} onOpenChange={setToggleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {templateToToggle?.status === 'active' ? t('templates:disable_title') : t('templates:enable_title')}
            </DialogTitle>
            <DialogDescription>
              {templateToToggle?.status === 'active'
                ? t('templates:disable_message', { title: templateToToggle?.template_title })
                : t('templates:enable_message', { title: templateToToggle?.template_title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToggleDialogOpen(false)} disabled={toggling}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant={templateToToggle?.status === 'active' ? 'destructive' : 'default'}
              className={templateToToggle?.status !== 'active' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
              onClick={handleToggleConfirm}
              disabled={toggling}
            >
              {toggling
                ? (templateToToggle?.status === 'active' ? t('templates:disabling') : t('templates:enabling'))
                : (templateToToggle?.status === 'active' ? t('templates:disable_confirm') : t('templates:enable_confirm'))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {templateToSend && (
        <SendTemplateModal
          open={sendModalOpen}
          onOpenChange={(open) => {
            setSendModalOpen(open);
            if (!open) setTemplateToSend(null);
          }}
          templateId={templateToSend.id}
          templateTitle={templateToSend.template_title}
          recipientCount={templateToSend.recipient_count}
          onSendComplete={() => {
            setSendModalOpen(false);
            setTemplateToSend(null);
            fetchTemplates();
          }}
        />
      )}
    </div>
  );
};

export default Templates;
